// IMPORTAZIONI E CONFIGURAZIONI
import React from 'react';
import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet";
import he from 'he';

// MODULO CSS
import styles from './BlogDetail.module.css';

// HOOKS
import useGetArticoloById from '../../hooks/useGetArticoloById';
import { useGetAgencyDetails } from "../../hooks/useGetAgencyDetails";
import useAddArticleView from '../../hooks/useAddArticleView';

// COMPONENTI
import { appConfig } from "../../config/appConfig";
import WidgetPartners from "../Widgets/WidgetPartners/WidgetPartners";
import WidgetContacts from "../Widgets/WidgetContacts/WidgetContacts";
import LoadingIndicator from "../Loaders/LoadingIndicator";
import NotFound from "../Error/NotFound";

// CONFIGURAZIONI E UTILS
import { LanguageCode } from '../../config/i18n/propertyTranslations';

// Helper per formattare date
const formatDate = (dateString: string | null, t: (key: string) => string): string => {
    if (!dateString) return t('blog.date.unavailable');
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return t('blog.date.invalid');
        }
        return date.toLocaleDateString('it-IT', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return t('blog.date.invalid');
    }
};

const stripHtml = (value: string): string => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

interface BlogDetailProps {
    agencyId: number;
}

const BlogDetail: React.FC<BlogDetailProps> = ({ agencyId }) => {
    const { t, i18n } = useTranslation();

    const currentLanguage = useMemo(() => {
        const supportedLanguages: ReadonlyArray<LanguageCode> = ['it', 'en', 'de', 'sq', 'ru', 'fr', 'es', 'pt'];

        // Se il multilingua è attivo, proviamo a leggere la lingua dall'URL come prima cosa
        if (appConfig.integrations.multilang) {
            const langFromUrl = location.pathname.split("/").filter(Boolean)[0];
            if ((supportedLanguages as ReadonlyArray<string>).includes(langFromUrl)) {
                return langFromUrl as LanguageCode;
            }
        }

        const i18nLang = i18n.language.split('-')[0];
        if ((supportedLanguages as ReadonlyArray<string>).includes(i18nLang)) {
            return i18nLang as LanguageCode;
        }

        return 'it' as LanguageCode;
    }, [location.pathname, i18n.language]);

    const { articleSlug: articleParam } = useParams<{ articleSlug: string }>();

    // Salva Id dallo slug dell'articolo
    const articleIdParam = useMemo(() => {
        if (!articleParam) return null;
        const numericId = Number(articleParam.split("-")[0]);
        return isNaN(numericId) ? null : numericId;
    }, [articleParam]);

    // --- Fetch Dati Agenzia ---
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);
    // --- Fine Fetch Dati Agenzia ---

    // --- Fetch Articolo ---
    const { articolo, isLoading: articleLoading, error: articleError, } = useGetArticoloById({
        id: Number(articleIdParam),
        agencyId: agencyId,
        enabled: !!articleIdParam
    });
    // --- Fine Fetch Articolo ---

    // --- Parteners per Widget ---
    const staticPartnerUrl = agencyId ? `http://www.casain24ore.it/fromsito.php?agReg=${agencyId}` : 'http://www.casain24ore.it';
    const staticPartner = { url: staticPartnerUrl, data: { imgSrc: '/assets/images/c24.png', imgAlt: 'Network Immobiliare' } };
    const rawDynamicPartnersData = appConfig.content.partners;
    const validDynamicPartnersData = rawDynamicPartnersData.filter(p => p && p.imgSrc && p.imgSrc.trim() !== '');
    const formattedDynamicPartners = validDynamicPartnersData.map(p => ({ url: p.url || '#', data: { imgSrc: p.imgSrc as string, imgAlt: p.imgAlt || 'Partner Logo' } }));
    const allPartnersForWidget = [staticPartner, ...formattedDynamicPartners];
    // --- Fine Parteners per Widget ---

    // INSERT VISUALIZZAZIONE / LETTURA ARTICOLO
    const { message, error, addArticleView } = useAddArticleView({
        id: Number(articleIdParam),
        agencyId: agencyId,
        enabled: true,
    });

    // Al caricamento della pagina aggiunge una visualizzazione
    useEffect(() => {
        addArticleView();
        if (error) {
            console.error("Error adding article view:", error);
        } else {
            console.log(message);
        }
    }, []);

    // --- Gestione Stati di Caricamento ed Errore ---
    if (!articleIdParam) {
        return <NotFound message={t('blogPage.detail.notFound')} />;
    }

    if (agencyLoading || articleLoading) return <LoadingIndicator />;

    if (agencyError) {
        console.error("Errore caricamento dati agenzia:", agencyError);
        return <NotFound message={`${t('blogPage.loadingError')}`} />;
    }

    if (articleError) {
        console.error("Errore caricamento articolo:", articleError, "ID Param:", articleIdParam);
        const message = articleError === "Articolo non trovato."
            ? t('blogPage.detail.notFound')
            : t('blogPage.detail.errorLoading');
        return <NotFound message={message} />;
    }

    if (!articolo) return <LoadingIndicator />;

    // --- Rendering del Contenuto dell'Articolo ---
    const imageUrl = articolo.foto ? `https://www.casagest24.it/${articolo.foto}` : '/images/blog/placeholder.jpg';
    const articleTitle = he.decode(articolo.titolo || '').trim() || `${t('blogPage.detail.title')}`;
    const articleDescription = stripHtml(he.decode(articolo.testoRidotto || articolo.testo || '')).slice(0, 220);
    const canonicalOrigin = typeof window !== 'undefined' ? window.location.origin : '';
    const canonicalUrl = canonicalOrigin + (appConfig.integrations.multilang ? `/${currentLanguage}` : "") + `/articolo/${articolo.id}-${articolo.seoString ?? 'articolo'}`;
    return (
        <>
            <Helmet>
                <title>{articleTitle}</title>
                <meta name="description" content={articleDescription} />
                <meta property="og:title" content={articleTitle} />
                <meta property="og:description" content={articleDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={imageUrl}/>
                <meta property="og:type" content="article" />
                <link rel="canonical" href={canonicalUrl} />
            </Helmet>
            <section className={`${styles.blogDetailSection} space-ptb blog-detail-page`}>
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12 col-xl-10">
                            {/* Titolo */}
                            <div className={`${styles.titleContainer} blog-post-title mb-3`}>
                                <h1 className={styles.title}>{he.decode(articolo.titolo)}</h1>
                            </div>

                            {/* Meta Info (Categoria, Data) */}
                            <div className="blog-post-details mb-3 pb-3 border-bottom">
                                <div className="d-flex justify-content-between align-items-center flex-wrap">
                                    {articolo.nomeCategoria && (
                                        <div className="blog-post-category mb-2 mb-sm-0">
                                            <Link to={`/blog/categoria/${articolo.categoriaSeoString || articolo.categoriaId}`}>
                                                <i className="fas fa-folder-open mr-1"></i>
                                                <strong>{articolo.nomeCategoria}</strong>
                                            </Link>
                                        </div>
                                    )}
                                    <div className={`${styles.timeContainer} blog-post-time`}>
                                        <i className="far fa-clock mr-1"></i>
                                        {formatDate(articolo.dataPubblicazione, t)}
                                        {/* Potremmo mostrare anche modifyAt se rilevante */}
                                        {/* {articolo.modifyAt && ` (Mod: ${formatDate(articolo.modifyAt)})`} */}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Colonna Contenuto Articolo */}
                        <div className="col-12 col-xl-8">
                            <div className={`${styles.blogPost} blog-post mb-3`}>
                                {/* Immagine Principale */}
                                {articolo.foto && (
                                    <div className={`${styles.imageContainer} blog-post-image mb-4`}>
                                        <img className={`${styles.image} img-fluid w-100`} src={imageUrl} alt={`${articolo.titolo || t('blogPage.article.imageAltFallback')}`} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="col-12 col-xl-10 mb-3">
                            <div className="blog-post-content">

                                {/* Testo Completo dell'Articolo */}
                                <div className={styles.contentContainer}>
                                    <div dangerouslySetInnerHTML={{ __html: articolo.testo }} />
                                </div>

                                {/* (Opzionale) Footer Post: Tags, Condivisione Social, etc. */}
                                {/* <div className="blog-post-footer mt-4 pt-3 border-top"> ... </div> */}

                                {/* (Opzionale) Link per tornare alla lista blog */}
                                <div className={`${styles.backButtonContainer}`}>
                                    <Link to="/blog" className={`${styles.backButton} btn btn-primary`}>
                                        <i className="fas fa-arrow-left mr-2"></i>{t('blogPage.backToList')}
                                    </Link>
                                </div>
                            </div>
                        </div>

                        {/* Colonna Sidebar (Widgets) */}
                        <div className="col-12 col-lg-6">
                            <WidgetContacts
                                agencyDataAddress={agency?.address_details?.address ?? ''}
                                agencyDataCity={agency?.address_details?.city_name ?? ''}
                                agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                agencyDataEmail={appConfig.agency.agencyEmail}
                                agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                agencyDataPhone1={agency?.phone1 ?? ''}
                                agencyDataPhone2={agency?.phone2 ?? ''}
                                agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                agencyDataMobile={agency?.mobile ?? ''}
                                agencyDataMobile2={agency?.mobile2 ?? ''}
                                agencyDataFax={agency?.fax ?? ''}
                                agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                agencyDataFacebook={appConfig.socialLinks.facebook}
                                agencyDataInstagram={appConfig.socialLinks.instagram}
                                agencyDataTwitter={appConfig.socialLinks.twitter}
                                agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                agencyDataPinterest={appConfig.socialLinks.pinterest}
                                agencyDataYoutube={appConfig.socialLinks.youtube}
                            />
                        </div>
                        <div className="col-12 col-lg-6">
                            <WidgetPartners partners={allPartnersForWidget} />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default BlogDetail;
